
import { useState } from 'react';
import { Star, MapPin, Clock } from 'lucide-react';
import BookingModal from '../booking/BookingModal';

interface ShopCardProps {
  shop: {
    id: string;
    name: string;
    image: string;
    rating: number;
    reviewCount: number;
    location: string;
    distance: string;
    status: 'open' | 'closed';
    priceRange: string;
    phoneNumber?: string;
  };
}

const ShopCard = ({ shop }: ShopCardProps) => {
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&fit=crop&q=80&w=800';
    target.onerror = null;
  };

  const handleBookNow = () => {
    setBookingModalOpen(true);
  };

  // Extract city from location (assuming format like "City, Palestine")
  const city = shop.location.split(',')[0].trim();

  return (
    <>
      <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 card-hover border border-autocare-gray-medium/20">
        <div className="relative h-48">
          <img 
            src={shop.image} 
            alt={shop.name}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
          <div className="absolute top-3 right-3 px-2 py-1 text-xs font-semibold rounded-full bg-white shadow-md">
            {shop.priceRange}
          </div>
        </div>
        
        <div className="p-5">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-semibold text-autocare-blue">{shop.name}</h3>
            <div className="flex items-center">
              <Star className="h-4 w-4 text-autocare-yellow fill-autocare-yellow" />
              <span className="text-sm font-medium ml-1">{shop.rating}</span>
              <span className="text-sm text-gray-500 ml-1">({shop.reviewCount})</span>
            </div>
          </div>
          
          <div className="flex flex-col gap-1 text-gray-500 mb-3">
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="text-sm truncate">{shop.location}</span>
              <span className="text-sm ml-auto font-medium">{shop.distance}</span>
            </div>
            {shop.phoneNumber && (
              <div className="flex items-center text-xs mt-1 text-autocare-blue">
                <span className="font-semibold mr-1">هاتف:</span>
                <a
                  href={`tel:${shop.phoneNumber}`}
                  className="underline hover:text-autocare-blue-light transition-colors"
                >
                  {shop.phoneNumber}
                </a>
              </div>
            )}
          </div>
          
          <div className="flex items-center mb-4">
            <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
            <span className={`text-sm ${shop.status === 'open' ? 'text-green-600' : 'text-red-500'}`}>
              {shop.status === 'open' ? 'مفتوح الآن' : 'مغلق'}
            </span>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleBookNow}
              className="flex-1 flex items-center justify-center px-4 py-2 rounded-lg bg-autocare-blue text-white font-medium hover:bg-autocare-blue-light transition-colors"
            >
              احجز الآن
            </button>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        shopName={shop.name}
        shopCity={city}
        isOpen={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
      />
    </>
  );
};

export default ShopCard;
